import type {
  ApplicationStatus,
  CustomerOrderGroup,
  DeadlineState,
  NotificationDigest,
  OrderStage,
  OrderStatus,
} from '../types';

export const orderLabels: Record<OrderStatus, string> = {
  WAITING_PAYMENT: 'Ожидается оплата',
  QUEUED: 'В очереди',
  IN_PROGRESS: 'В работе',
  REVIEW: 'На согласовании',
  WAITING_FINAL_PAYMENT: 'Ожидается доплата',
  COMPLETED: 'Завершён',
  PAUSED: 'На паузе',
  CANCELED: 'Отменён',
};

export const applicationLabels: Record<ApplicationStatus, string> = {
  NEW: 'Новая',
  NEEDS_INFO: 'Нужно уточнение',
  ACCEPTED: 'Принята',
  REJECTED: 'Отклонена',
  CANCELED: 'Отменена',
};

export const stageLabels: Record<OrderStage, string> = {
  PREPARATION: 'Подготовка',
  QUEUED: 'В очереди',
  SKETCH: 'Скетч',
  SKETCH_REVIEW: 'Скетч на согласовании',
  LINEART: 'Контур',
  COLORING: 'Цвет',
  DETAILING: 'Детализация',
  WAITING_REVISIONS: 'Ожидаются правки',
  REVISION: 'Внесение правок',
  FINAL_REVIEW: 'Финальное согласование',
  READY: 'Готово',
  DELIVERED: 'Передано',
  COMPLETED: 'Завершён',
  PAUSED: 'На паузе',
  CANCELED: 'Отменён',
};

export const customerGroupLabels: Record<CustomerOrderGroup, string> = {
  needsAction: 'Требуют действия',
  inProgress: 'Выполняются',
  waitingArtist: 'Ожидают художника',
  completed: 'Завершены',
};

export const digestLabels: Record<NotificationDigest, string> = {
  OFF: 'Не напоминать',
  DAILY: 'Каждый день',
  EVERY_3_DAYS: 'Раз в три дня',
  WEEKLY: 'Раз в неделю',
};

export const deadlineLabels: Record<DeadlineState, string> = {
  NORMAL: 'В срок',
  DUE_SOON: 'Скоро дедлайн',
  DUE_TODAY: 'Дедлайн сегодня',
  OVERDUE: 'Просрочен',
  NO_DEADLINE: 'Без срока',
};

export function deadlineTone(state: DeadlineState): 'neutral' | 'warning' | 'danger' {
  if (state === 'OVERDUE') return 'danger';
  if (state === 'DUE_TODAY' || state === 'DUE_SOON') return 'warning';
  return 'neutral';
}

/** Как выглядит сообщение об оплате в списке: одинаково в обоих кабинетах. */
export const paymentClaimLabels = {
  PENDING: 'Ждёт художника',
  CONFIRMED: 'Подтверждено',
  REJECTED: 'Не подтверждено',
} as const;

export function paymentClaimTone(status: keyof typeof paymentClaimLabels): string {
  if (status === 'CONFIRMED') return 'text-success';
  return status === 'REJECTED' ? 'text-warning' : 'muted';
}

/**
 * Срок услуги словами: «около 10 дн.» или «7–14 дн.». Точных обещаний тут нет —
 * это ориентир до заявки, настоящий срок художник назначает сам.
 */
export function serviceDuration(service: {
  durationMinDays: number | null;
  durationMaxDays: number | null;
}): string | null {
  const { durationMinDays: min, durationMaxDays: max } = service;

  if (min && max && min !== max) return `${min}–${max} дн.`;
  const single = max ?? min;

  return single ? `около ${single} дн.` : null;
}

export function money(value: string | number, currency = 'RUB'): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

/**
 * Часовой пояс, заданный сдвигом, приходит как зона `Etc/GMT∓H` с обратным
 * знаком. Показывать её как есть нельзя — читается как противоположный сдвиг.
 */
export function timezoneLabel(timezone: string): string {
  const offset = /^Etc\/GMT([+-])(\d{1,2})$/.exec(timezone);

  if (offset) {
    return `UTC${offset[1] === '+' ? '-' : '+'}${offset[2]}`;
  }

  return timezone === 'UTC' ? 'UTC+0' : timezone;
}

export function shortDate(value: string | null): string {
  if (!value) return 'Без срока';
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(
    new Date(`${value.slice(0, 10)}T00:00:00`),
  );
}

/**
 * Сегодняшняя дата для `min` у `<input type="date">`. Считается по часам
 * устройства, а не в UTC: пользователь выбирает дату своего календаря, и
 * запретить нужно ровно то, что для него уже прошло.
 */
export function todayInput(now: Date = new Date()): string {
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function dateTime(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export const nextOrderStatuses: Record<OrderStatus, OrderStatus[]> = {
  WAITING_PAYMENT: ['QUEUED', 'CANCELED'],
  QUEUED: ['IN_PROGRESS', 'PAUSED', 'CANCELED'],
  IN_PROGRESS: ['REVIEW', 'PAUSED', 'CANCELED'],
  REVIEW: ['IN_PROGRESS', 'WAITING_FINAL_PAYMENT', 'COMPLETED', 'CANCELED'],
  WAITING_FINAL_PAYMENT: ['COMPLETED', 'PAUSED', 'CANCELED'],
  PAUSED: ['QUEUED', 'IN_PROGRESS', 'REVIEW', 'WAITING_FINAL_PAYMENT', 'CANCELED'],
  COMPLETED: [],
  CANCELED: [],
};
